import { decryptField, encryptField } from '../crypto/aesgcm';
import { bytesToBase64Url, randomBytes } from '../crypto/encoding';
import { PH_VAT_RATE, roundCentavos } from '../invoice/vat';
import type { CruzFindVault, ExpenseCategory, ExpenseLedgerRow } from './schema';

export interface ExpenseEntry {
  id: string;
  date: number;
  vendorName: string;
  vendorTin?: string;
  category: ExpenseCategory;
  amountGross: number;
  amountNet: number;
  vatInput: number;
  isVatReceipt: boolean;
  attachmentBlob?: string;
}

export interface ExpenseInput {
  date: number;
  vendorName: string;
  vendorTin?: string;
  category: ExpenseCategory;
  amountGross: number;
  isVatReceipt: boolean;
  attachmentBlob?: string;
}

function newExpenseId(): string {
  return bytesToBase64Url(randomBytes(16));
}

export function deriveExpenseAmounts(
  amountGross: number,
  isVatReceipt: boolean,
): { amountNet: number; vatInput: number } {
  if (!isVatReceipt) {
    return { amountNet: roundCentavos(amountGross), vatInput: 0 };
  }
  const net = roundCentavos(amountGross / (1 + PH_VAT_RATE));
  const vat = roundCentavos(amountGross - net);
  return { amountNet: net, vatInput: vat };
}

export class ExpenseLedgerRepo {
  constructor(
    private readonly db: CruzFindVault,
    private readonly aesKey: CryptoKey,
  ) {}

  async add(input: ExpenseInput): Promise<ExpenseEntry> {
    if (!Number.isFinite(input.amountGross) || input.amountGross <= 0) {
      throw new Error('amountGross must be a positive number');
    }
    const { amountNet, vatInput } = deriveExpenseAmounts(input.amountGross, input.isVatReceipt);

    const encryptedVendorName = await encryptField(this.aesKey, input.vendorName);
    const encryptedVendorTin =
      input.vendorTin && input.vendorTin.length > 0
        ? await encryptField(this.aesKey, input.vendorTin)
        : undefined;
    const encryptedAttachmentBlob =
      input.attachmentBlob && input.attachmentBlob.length > 0
        ? await encryptField(this.aesKey, input.attachmentBlob)
        : undefined;

    const row: ExpenseLedgerRow = {
      id: newExpenseId(),
      date: input.date,
      encryptedVendorName,
      encryptedVendorTin,
      category: input.category,
      amountGross: roundCentavos(input.amountGross),
      amountNet,
      vatInput,
      isVatReceipt: input.isVatReceipt,
      encryptedAttachmentBlob,
    };
    await this.db.expense_ledger.add(row);

    return {
      id: row.id,
      date: row.date,
      vendorName: input.vendorName,
      vendorTin: input.vendorTin,
      category: row.category,
      amountGross: row.amountGross,
      amountNet: row.amountNet,
      vatInput: row.vatInput,
      isVatReceipt: row.isVatReceipt,
      attachmentBlob: input.attachmentBlob,
    };
  }

  async list(): Promise<ExpenseEntry[]> {
    const rows = await this.db.expense_ledger.orderBy('date').toArray();
    return Promise.all(rows.map((r) => this.decryptRow(r)));
  }

  async remove(id: string): Promise<void> {
    await this.db.expense_ledger.delete(id);
  }

  private async decryptRow(row: ExpenseLedgerRow): Promise<ExpenseEntry> {
    const vendorName = await decryptField(this.aesKey, row.encryptedVendorName);
    const vendorTin = row.encryptedVendorTin
      ? await decryptField(this.aesKey, row.encryptedVendorTin)
      : undefined;
    const attachmentBlob = row.encryptedAttachmentBlob
      ? await decryptField(this.aesKey, row.encryptedAttachmentBlob)
      : undefined;
    return {
      id: row.id,
      date: row.date,
      vendorName,
      vendorTin,
      category: row.category,
      amountGross: row.amountGross,
      amountNet: row.amountNet,
      vatInput: row.vatInput,
      isVatReceipt: row.isVatReceipt,
      attachmentBlob,
    };
  }
}
